import {
    Button,
    createStyles,
    FormHelperText,
    Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import * as React from "react";

import { baseColors, Breakpoint, RelodeTheme, Text, useDeviceSize } from "../";
import { InfoIcon } from "../../assets/icons";
import { SelectableButtonItem } from "./types";

const useButtonStyles = makeStyles<RelodeTheme, SelectableButtonProps>(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                minHeight: "51px",
                color: baseColors.black,
                backgroundColor: baseColors.cool_gray,
                fontFamily: [
                    // eslint-disable-next-line quotes
                    '"hkgrotesk_regular"',
                    "Helvetica",
                    "sans-serif",
                ].join(","),
                fontWeight: "normal",
                fontStyle: "normal",
                padding: 5,
                "&:disabled": {
                    color: baseColors.inactive_gray,
                    backgroundColor: baseColors.cool_gray,
                },
            },
            largeButton: {
                minHeight: "71px",
            },
            mobileMediumButton: {
                minHeight: "120px",
            },
            mobileLargeButton: {
                minHeight: "160px",
            },
            largeDetailsButton: {
                minHeight: "71px",
                display: "flex",
                flexDirection: "column",
            },
            outlinedButton: {
                border: `1px solid ${baseColors.magenta}`,
                backgroundColor: baseColors.white,
                padding: 5,
                "&:disabled": {
                    border: `1px solid ${baseColors.magenta}`,
                    color: baseColors.inactive_gray,
                    backgroundColor: baseColors.white,
                },
            },
            disclaimer: {
                margin: theme.spacing(2, 0),
                lineHeight: "20px",
                [theme.breakpoints.up("md")]: {
                    marginLeft: theme.spacing(2),
                },
            },
            tooltipIcon: {
                verticalAlign: "middle",
            },
            label: {
                color: theme.relode.colors.label.main,
            },
            tinyButton: {
                minHeight: "unset",
                height: 30,
            },
            tinyButtonLabel: {
                marginTop: -4,
            },
            extraLargeButton: {
                minHeight: "120px",
                display: "flex",
                flexDirection: "row",
                padding: "4px 10px",
            },
        }),
    { name: "SelectableButton" }
);

const useStyles = makeStyles<RelodeTheme, SelectableButtonsListProps>(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            error: {
                color: theme.palette.error.main,
            },
            helperText: {
                marginBottom: theme.spacing(2),
            },
            container: {
                display: "grid",
                gridGap: theme.spacing(1),
            },
            smallColumns: {
                gridTemplateColumns: props =>
                    `repeat(${
                        props.compressed ? "auto-fill" : "auto-fit"
                    }, minmax(60px, 1fr))`,
            },
            mediumColumns: {
                [theme.breakpoints.up("md")]: {
                    gridTemplateColumns: props =>
                        `repeat(${
                            props.compressed ? "auto-fill" : "auto-fit"
                        }, minmax(100px, 1fr))`,
                }
            },
            largeColumns: {
                gridTemplateColumns: props =>
                    `repeat(${
                        props.compressed ? "auto-fill" : "auto-fit"
                    }, minmax(120px, 1fr))`,
            },
            tiny: {
                gridTemplateColumns: props =>
                    `repeat(${
                        props.compressed ? "auto-fill" : "auto-fit"
                    }, minmax(60px, 1fr))`,
                "& > button": {},
            },
            largeDetailsColumns: {
                gridTemplateColumns: props =>
                    `repeat(${
                        props.compressed ? "auto-fill" : "auto-fit"
                    }, minmax(200px, 1fr))`,
            },
            fullWidthColumns: {
                gridTemplateColumns: "1fr",
            },
            button: {},
        }),
    { name: "SelectableButtonsList" }
);

interface SelectableButtonsListProps {
    id: string;
    items: SelectableButtonItem[];
    size?: "sm" | "md" | "lg" | "fullWidth" | "tiny" | "xl";
    onClick: (item: SelectableButtonItem) => void;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    className?: string;
    compressed?: boolean;
}

export const SelectableButtonsList = (props: SelectableButtonsListProps) => {
    const {
        id,
        items,
        size = "md",
        onClick,
        error,
        helperText,
        disabled = false,
        className,
    } = props;
    const classes = useStyles(props);
    const device = useDeviceSize();
    const withDetailsMobile =
        device === "mobile" && items.some(item => !!item.details);

    const helperTextClasses = classnames(classes.helperText, {
        [classes.error]: error,
    });

    const buttonsListClass = classnames(classes.container, {
        [classes.tiny]: size === "tiny",
        [classes.smallColumns]: size === "sm",
        [classes.mediumColumns]: size === "md",
        [classes.largeColumns]: size === "lg",
        [classes.fullWidthColumns]: size === "fullWidth" || size === "xl",
        [classes.largeDetailsColumns]: withDetailsMobile,
    });

    const rootClasses = classnames(classes.root, className);

    return (
        <div className={rootClasses}>
            <div
                className={buttonsListClass}
                data-cy={`${id}-selectableButtonList`}
            >
                {items.map(item => (
                    <SelectableButton
                        key={item.id}
                        item={item}
                        onClick={onClick}
                        disabled={disabled}
                        size={size}
                        withDetailsMobile={withDetailsMobile}
                    />
                ))}
            </div>
            {error && (
                <FormHelperText
                    id={`${id}-selectableButton-helper-text`}
                    className={helperTextClasses}
                >
                    {helperText}
                </FormHelperText>
            )}
        </div>
    );
};

export interface SelectableButtonProps {
    size?: "sm" | "md" | "lg" | "fullWidth" | "tiny" | "xl";
    onClick: (item: SelectableButtonItem) => void;
    disabled?: boolean;
    item: SelectableButtonItem;
    withDetailsMobile?: boolean;
}

export const SelectableButton = (props: SelectableButtonProps) => {
    const {
        item: { id, label, selected, details },
        item,
        size,
        disabled,
        onClick,
        withDetailsMobile,
    } = props;
    const classes = useButtonStyles(props);

    const device = useDeviceSize();

    const buttonClass = classnames(classes.button, {
        [classes.largeButton]: size === "lg" || size === "fullWidth",
        [classes.tinyButton]: size === "tiny",
        [classes.extraLargeButton]: size === "xl",
    });

    const mobileButtonClass = classnames(classes.button, {
        [classes.mobileMediumButton]: size === "md",
        [classes.mobileLargeButton]: size === "lg" || size === "fullWidth",
    });

    const desktopDetails = (item: SelectableButtonItem) => (
        <Tooltip
            placement="right"
            title={item.details}
            className={classes.disclaimer}
        >
            <div>
                <InfoIcon
                    fontSize="inherit"
                    color="primary"
                    className={classes.tooltipIcon}
                />
            </div>
        </Tooltip>
    );

    const mobileDetails = (item: SelectableButtonItem) => (
        <div className={classes.disclaimer}>
            <Text variant="caption" className={classes.label}>
                {item.details}
            </Text>
        </div>
    );

    return (
        <Button
            data-cy={`${id}-selectableButton`}
            data-cy-value={selected ? "selected" : "unselected"}
            key={`${id}`}
            id={`${id}-selectableButton`}
            className={device === "mobile" ? mobileButtonClass : buttonClass}
            classes={{
                outlined: classes.outlinedButton,
                label: classnames({
                    [classes.largeDetailsButton]: withDetailsMobile,
                    [classes.tinyButtonLabel]: size === "tiny",
                }),
            }}
            variant={selected ? "outlined" : "contained"}
            onClick={() => onClick(item)}
            disabled={disabled}
            size="small"
        >
            {label}

            {details ? (
                <>
                    <Breakpoint desktop>{desktopDetails(item)}</Breakpoint>
                    <Breakpoint mobile>{mobileDetails(item)}</Breakpoint>
                </>
            ) : null}
        </Button>
    );
};
